import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api, json } from '../lib/api';
import { Notice } from '../components/Notice';
import type { SavedPlayer } from '../types';

interface FormState {
  name: string;
  playType: 'singles' | 'doubles';
  partnerMode: 'fixed' | 'mixed';
  eventType: 'social' | 'tournament';
  courts: number;
  rounds: number;
  scoringEnabled: boolean;
  scheduledDate: string;
  venue: string;
  playerText: string;
}

const initial: FormState = { name: '', playType: 'doubles', partnerMode: 'mixed', eventType: 'social', courts: 2, rounds: 6, scoringEnabled: true, scheduledDate: '', venue: '', playerText: '' };

function Choice<T extends string>({ value, current, label, hint, onChange }: { value: T; current: T; label: string; hint: string; onChange: (value: T) => void }) {
  const selected = value === current;
  return <button type="button" onClick={() => onChange(value)} className={`rounded-2xl border p-4 text-left transition ${selected ? 'border-[var(--primary)] bg-[var(--primary)] text-white shadow-lg' : 'border-slate-200 bg-white hover:border-slate-300'}`}><b className="block text-sm">{label}</b><span className={`mt-1 block text-xs leading-5 ${selected ? 'text-white/60' : 'text-slate-400'}`}>{hint}</span></button>;
}

export function CreateTournamentPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [savedPlayers, setSavedPlayers] = useState<SavedPlayer[]>([]);
  const [rosterOpen, setRosterOpen] = useState(false);
  const [rosterBusy, setRosterBusy] = useState(false);
  const names = useMemo(() => form.playerText.split('\n').map((name) => name.trim()).filter(Boolean), [form.playerText]);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((current) => ({ ...current, [key]: value }));

  const loadSavedPlayers = useCallback(async () => {
    try {
      const result = await api<{ players: SavedPlayer[] }>('/saved-players');
      setSavedPlayers(result.players);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { void loadSavedPlayers(); }, [loadSavedPlayers]);

  const addSavedPlayer = (playerName: string) => {
    const current = names;
    if (!current.includes(playerName)) {
      set('playerText', [...current, playerName].join('\n'));
    }
  };

  const addAllSaved = () => {
    const current = new Set(names);
    const toAdd = savedPlayers.filter((p) => !current.has(p.name)).map((p) => p.name);
    if (toAdd.length > 0) set('playerText', [...names, ...toAdd].join('\n'));
  };

  const deleteSavedPlayer = async (id: string) => {
    setRosterBusy(true);
    try {
      await api(`/saved-players/${id}`, { method: 'DELETE' });
      await loadSavedPlayers();
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not remove player.'); }
    finally { setRosterBusy(false); }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault(); setBusy(true); setError(''); setSuggestions([]);
    try {
      const players = names.map((name, index) => ({ name, teamId: form.playType === 'doubles' && form.partnerMode === 'fixed' ? `team-${Math.floor(index / 2) + 1}` : null, seed: index + 1 }));
      const created = await api<{ id: string }>('/tournaments', { method: 'POST', ...json({ ...form, scheduledDate: form.scheduledDate || undefined, venue: form.venue || undefined, players }) });
      await api(`/tournaments/${created.id}/generate`, { method: 'POST' });
      navigate(`/tournaments/${created.id}`);
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Tournament could not be created.');
      if (value instanceof ApiError && value.details && typeof value.details === 'object' && 'suggestions' in value.details) setSuggestions((value.details as { suggestions: string[] }).suggestions ?? []);
    } finally { setBusy(false); }
  };

  // Players not yet in the textarea
  const availableSaved = savedPlayers.filter((p) => !names.includes(p.name));

  return <form className="space-y-6" onSubmit={submit}>
    <div><p className="eyebrow">New event</p><h2 className="mt-1 text-3xl font-extrabold tracking-tight brand-text">Build your draw</h2><p className="mt-2 text-sm text-slate-500">Set the format, add players in seed order, and the engine will handle the rest.</p></div>
    {error && <Notice><p>{error}</p>{suggestions.length > 0 && <ul className="mt-2 list-disc pl-5">{suggestions.map((item) => <li key={item}>{item}</li>)}</ul>}</Notice>}

    <section className="surface p-5 sm:p-7">
      <div className="mb-5 flex items-center gap-3"><span className="grid h-9 w-9 place-items-center rounded-xl brand-bg text-sm font-extrabold text-white">1</span><div><h3 className="font-extrabold brand-text">Tournament details</h3><p className="text-xs text-slate-400">Give the session a clear name and purpose.</p></div></div>
      <label className="block mb-4"><span className="label">Tournament name</span><input className="field" placeholder="Friday Night Social" value={form.name} onChange={(event) => set('name', event.target.value)} required /></label>
      <div className="mb-4 grid gap-4 sm:grid-cols-2">
        <label><span className="label">Scheduled Date (Optional)</span><input className="field" type="date" value={form.scheduledDate} onChange={(event) => set('scheduledDate', event.target.value)} /></label>
        <label><span className="label">Venue (Optional)</span><input className="field" placeholder="Pickleball Central" value={form.venue} onChange={(event) => set('venue', event.target.value)} /></label>
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <Choice value="social" current={form.eventType} label="Social" hint="Round-robin play and a live table." onChange={(value) => set('eventType', value)} />
        <Choice value="tournament" current={form.eventType} label="Tournament" hint="Social rounds followed by a seeded knockout." onChange={(value) => set('eventType', value)} />
      </div>
    </section>

    <section className="surface p-5 sm:p-7">
      <div className="mb-5 flex items-center gap-3"><span className="grid h-9 w-9 place-items-center rounded-xl brand-bg text-sm font-extrabold text-white">2</span><div><h3 className="font-extrabold brand-text">Play format</h3><p className="text-xs text-slate-400">Choose who plays together.</p></div></div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Choice value="singles" current={form.playType} label="Singles" hint="One player on each side." onChange={(value) => set('playType', value)} />
        <Choice value="doubles" current={form.playType} label="Doubles" hint="Two players on each side." onChange={(value) => set('playType', value)} />
      </div>
      {form.playType === 'doubles' && <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Choice value="mixed" current={form.partnerMode} label="Changing partners" hint="Every partnership is unique in social rounds." onChange={(value) => set('partnerMode', value)} />
        <Choice value="fixed" current={form.partnerMode} label="Fixed partners" hint="Players stay in the entered pairs." onChange={(value) => set('partnerMode', value)} />
      </div>}
      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <label><span className="label">Courts</span><select className="field" value={form.courts} onChange={(event) => set('courts', Number(event.target.value))}>{[1,2,3,4].map((value) => <option key={value} value={value}>{value} court{value > 1 ? 's' : ''}</option>)}</select></label>
        <label><span className="label">Social rounds</span><input className="field" type="number" min="1" max="100" value={form.rounds} onChange={(event) => set('rounds', Number(event.target.value))} /></label>
        <label><span className="label">Score tracking</span><button type="button" onClick={() => set('scoringEnabled', !form.scoringEnabled)} className={`field flex items-center justify-between ${form.scoringEnabled ? 'brand-bg !text-white' : ''}`}><span>{form.scoringEnabled ? 'Enabled' : 'Disabled'}</span><span className={`h-5 w-9 rounded-full p-0.5 ${form.scoringEnabled ? 'bg-white/30' : 'bg-slate-200'}`}><span className={`block h-4 w-4 rounded-full bg-white transition ${form.scoringEnabled ? 'translate-x-4' : ''}`} /></span></button></label>
      </div>
    </section>

    <section className="surface p-5 sm:p-7">
      <div className="mb-5 flex items-center justify-between gap-3"><div className="flex items-center gap-3"><span className="grid h-9 w-9 place-items-center rounded-xl brand-bg text-sm font-extrabold text-white">3</span><div><h3 className="font-extrabold brand-text">Players</h3><p className="text-xs text-slate-400">One name per line, maximum 30.</p></div></div><span className={`chip ${names.length > 30 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{names.length}/30</span></div>

      {/* Saved players toolbar */}
      {savedPlayers.length > 0 && (
        <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[10px] font-extrabold uppercase text-slate-400 mr-1">Saved roster</p>
            <select
              className="field !min-h-8 !py-1 text-xs flex-1 min-w-[140px]"
              defaultValue=""
              onChange={(e) => { if (e.target.value) { addSavedPlayer(e.target.value); e.target.value = ''; } }}
            >
              <option value="">Quick-add a saved player…</option>
              {availableSaved.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
            {availableSaved.length > 0 && (
              <button type="button" onClick={addAllSaved} className="btn-ghost !min-h-8 !py-1 !px-3 text-xs">Add all ({availableSaved.length})</button>
            )}
            <button type="button" onClick={() => setRosterOpen(true)} className="text-xs font-bold text-slate-500 hover:text-slate-800 transition">Manage roster →</button>
          </div>
        </div>
      )}
      {savedPlayers.length === 0 && (
        <p className="mb-3 text-xs text-slate-400">Players you add here will be saved to your roster for quick re-use in future events.</p>
      )}

      <textarea className="field min-h-56 resize-y leading-7" placeholder={'Anele\nBianca\nChris\nDylan'} value={form.playerText} onChange={(event) => set('playerText', event.target.value)} required />
      {form.playType === 'doubles' && form.partnerMode === 'fixed' && names.length > 0 && <div className="mt-4 rounded-2xl bg-slate-50 p-4"><p className="label">Fixed pair preview</p><div className="grid gap-2 sm:grid-cols-2">{Array.from({ length: Math.ceil(names.length / 2) }, (_, index) => <div key={index} className="rounded-xl bg-white px-3 py-2 text-xs font-bold text-slate-600">Team {index + 1}: {names[index * 2] ?? '—'} + {names[index * 2 + 1] ?? 'Needs partner'}</div>)}</div></div>}
    </section>

    <div className="sticky bottom-20 z-20 rounded-3xl border border-white/80 bg-white/90 p-3 shadow-lift backdrop-blur-xl lg:bottom-4"><button className="btn-primary w-full" disabled={busy || names.length < (form.playType === 'singles' ? 2 : 4) || names.length > 30}>{busy ? 'Building your fair draw…' : 'Create & generate fixtures'}</button></div>

    {/* Manage Roster Modal */}
    {rosterOpen && (
      <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/55 p-0 backdrop-blur-sm sm:place-items-center sm:p-5" onMouseDown={(e) => { if (e.target === e.currentTarget) setRosterOpen(false); }}>
        <div className="w-full max-w-sm max-h-[85dvh] overflow-y-auto rounded-t-4xl bg-white p-6 pb-[max(1.5rem,calc(env(safe-area-inset-bottom)+1rem))] shadow-2xl sm:max-h-none sm:rounded-4xl sm:p-8">
          <div className="flex items-start justify-between mb-4">
            <div><p className="eyebrow">Your saved players</p><h3 className="mt-1 text-xl font-extrabold brand-text">Manage roster</h3></div>
            <button type="button" className="grid h-10 w-10 place-items-center rounded-xl bg-slate-100 text-xl" onClick={() => setRosterOpen(false)}>×</button>
          </div>
          <p className="text-sm text-slate-500 mb-4">These names are saved to your account and can be quickly added to any new event. Removing a player here does not affect past events.</p>
          {savedPlayers.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Your roster is empty. Players are automatically saved when you create events.</p>
          ) : (
            <ul className="space-y-2">
              {savedPlayers.map((p) => (
                <li key={p.id} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
                  <span className="text-sm font-bold text-slate-700">{p.name}</span>
                  <button type="button" disabled={rosterBusy} onClick={() => void deleteSavedPlayer(p.id)} className="text-xs font-bold text-red-500 hover:text-red-700 disabled:opacity-50">Remove</button>
                </li>
              ))}
            </ul>
          )}
          <button type="button" className="btn-ghost w-full mt-5" onClick={() => setRosterOpen(false)}>Done</button>
        </div>
      </div>
    )}
  </form>;
}
